import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, DatePicker, Row, Space, Statistic, Table, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import moment, { Moment } from 'moment';
import { formatNu } from '../utils/formatAmount';
import { exportSheetsToExcel } from '../utils/exportToExcel';
import {
  getGstFilingPack,
  GstFilingInvoiceRow,
  GstFilingPack,
  GstFilingPurchaseRow,
} from '../services/gstFilingService';
import './Reports.css';
import './GstFiling.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const GstFiling: React.FC = () => {
  const [range, setRange] = useState<[Moment, Moment]>([
    moment().startOf('month'),
    moment().endOf('day'),
  ]);
  const [pack, setPack] = useState<GstFilingPack | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const startDate = range[0].format('YYYY-MM-DD');
  const endDate = range[1].format('YYYY-MM-DD');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        setPack(await getGstFilingPack(startDate, endDate));
      } catch {
        setError('Could not load the GST filing pack.');
        setPack(null);
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [startDate, endDate]);

  const summary = pack?.summary;
  const invoices = pack?.tax_invoices || [];
  const purchases = pack?.purchases || [];

  const invoiceColumns: ColumnsType<GstFilingInvoiceRow> = [
    { title: 'Date', dataIndex: 'sale_date', width: 110 },
    { title: 'TAX INVOICE', dataIndex: 'tax_invoice_number', render: (v) => v || '—' },
    { title: 'Receipt', dataIndex: 'transaction_number' },
    { title: 'Taxable', dataIndex: 'taxable_amount', align: 'right', render: (v) => formatNu(v) },
    { title: 'Exempt', dataIndex: 'exempt_amount', align: 'right', render: (v) => formatNu(v) },
    { title: 'Zero-rated', dataIndex: 'zero_rated_amount', align: 'right', render: (v) => formatNu(v) },
    { title: 'Output GST', dataIndex: 'output_gst', align: 'right', render: (v) => formatNu(v) },
    { title: 'Buyer', dataIndex: 'buyer_name', render: (v, row) => v || (row.buyer_required ? 'Missing' : '—') },
    { title: 'CID', dataIndex: 'buyer_cid', render: (v) => v || '—' },
    { title: 'Buyer TPN', dataIndex: 'buyer_tpn', render: (v) => v || '—' },
  ];

  const purchaseColumns: ColumnsType<GstFilingPurchaseRow> = [
    { title: 'Date', dataIndex: 'received_date', width: 110 },
    { title: 'GRPO', dataIndex: 'grpo_number' },
    { title: 'Vendor', dataIndex: 'vendor_name', render: (v) => v || '—' },
    { title: 'Supplier invoice', dataIndex: 'supplier_invoice', render: (v) => v || '—' },
    { title: 'Taxable', dataIndex: 'taxable_amount', align: 'right', render: (v) => formatNu(v) },
    { title: 'Exempt', dataIndex: 'exempt_amount', align: 'right', render: (v) => formatNu(v) },
    { title: 'Zero-rated', dataIndex: 'zero_rated_amount', align: 'right', render: (v) => formatNu(v) },
    { title: 'Input GST', dataIndex: 'input_gst', align: 'right', render: (v) => formatNu(v) },
  ];

  const exportExcel = () => {
    if (!summary) return;
    exportSheetsToExcel(
      [
        {
          name: 'Summary',
          rows: [
            { Field: 'Period start', Value: summary.start_date },
            { Field: 'Period end', Value: summary.end_date },
            { Field: 'Supplier TPN', Value: summary.supplier_tpn || '' },
            { Field: 'TAX INVOICE count', Value: summary.tax_invoice_count },
            { Field: 'Output GST', Value: Number(summary.output_gst) },
            { Field: 'Taxable supplies', Value: Number(summary.taxable_amount) },
            { Field: 'Exempt supplies', Value: Number(summary.exempt_amount) },
            { Field: 'Zero-rated supplies', Value: Number(summary.zero_rated_amount) },
            { Field: 'Input GST (purchases)', Value: Number(summary.input_gst) },
            { Field: 'Net GST (output − input)', Value: Number(summary.net_gst) },
            { Field: 'Invoices missing buyer ID ≥ Nu. 50,000', Value: summary.invoices_missing_buyer },
          ],
        },
        {
          name: 'Tax invoices',
          rows: invoices.map((row) => ({
            Date: row.sale_date,
            'TAX INVOICE': row.tax_invoice_number || '',
            Receipt: row.transaction_number,
            'Taxable (ex-GST)': Number(row.taxable_amount),
            Exempt: Number(row.exempt_amount),
            'Zero-rated': Number(row.zero_rated_amount),
            'Output GST': Number(row.output_gst),
            Total: Number(row.total_amount),
            'Buyer name': row.buyer_name || '',
            CID: row.buyer_cid || '',
            'Buyer TPN': row.buyer_tpn || '',
            'Buyer address': row.buyer_address || '',
            'Buyer ID required': row.buyer_required,
            'Buyer ID complete': row.buyer_complete,
          })),
        },
        {
          name: 'Purchases input tax',
          rows: purchases.map((row) => ({
            Date: row.received_date,
            GRPO: row.grpo_number,
            Vendor: row.vendor_name || '',
            'Supplier invoice': row.supplier_invoice || '',
            'Taxable (ex-GST)': Number(row.taxable_amount),
            Exempt: Number(row.exempt_amount),
            'Zero-rated': Number(row.zero_rated_amount),
            'Input GST': Number(row.input_gst),
            Gross: Number(row.gross),
          })),
        },
      ],
      `GST-BITS-${summary.start_date}-to-${summary.end_date}`,
    );
  };

  return (
    <div className="gst-filing-page">
      <div className="gst-filing-head">
        <div>
          <Title level={3} style={{ margin: 0 }}>
            GST filing pack
          </Title>
          <Text type="secondary">
            Period totals for BITS: output GST, taxable / exempt / zero-rated, input tax from completed GRPOs.
          </Text>
        </div>
        <Space wrap>
          <RangePicker
            value={range}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) setRange([dates[0], dates[1]]);
            }}
            allowClear={false}
          />
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={exportExcel}
            disabled={!summary || !summary.gst_registered}
          >
            Excel for BITS
          </Button>
        </Space>
      </div>

      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} /> : null}
      {summary && !summary.gst_registered ? (
        <Alert
          type="info"
          showIcon
          message="This company is not GST-registered. Turn on GST in company settings to collect TAX INVOICES and build a filing pack."
          style={{ marginBottom: 16 }}
        />
      ) : null}
      {summary && summary.invoices_missing_buyer > 0 ? (
        <Alert
          type="warning"
          showIcon
          message={`${summary.invoices_missing_buyer} TAX INVOICE(s) of Nu. ${Number(summary.buyer_threshold).toLocaleString()} or more are missing buyer name and CID or TPN.`}
          style={{ marginBottom: 16 }}
        />
      ) : null}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Output GST" prefix="Nu." value={Number(summary?.output_gst || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Taxable supplies" prefix="Nu." value={Number(summary?.taxable_amount || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Exempt" prefix="Nu." value={Number(summary?.exempt_amount || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Zero-rated" prefix="Nu." value={Number(summary?.zero_rated_amount || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Input GST (purchases)" prefix="Nu." value={Number(summary?.input_gst || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="Net GST (output − input)" prefix="Nu." value={Number(summary?.net_gst || 0)} precision={2} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={8}>
          <Card className="reports-stat-card" loading={loading}>
            <Statistic title="TAX INVOICES" value={summary?.tax_invoice_count || 0} />
          </Card>
        </Col>
      </Row>

      <Card title="TAX INVOICES (output)" style={{ marginTop: 16 }} loading={loading}>
        <Table
          rowKey={(row) => `${row.transaction_number}-${row.sale_date}`}
          columns={invoiceColumns}
          dataSource={invoices}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1100 }}
          size="small"
        />
      </Card>
      <Card title="Purchases (input tax from completed GRPOs)" style={{ marginTop: 16 }} loading={loading}>
        <Table
          rowKey={(row) => `${row.grpo_number}-${row.received_date}`}
          columns={purchaseColumns}
          dataSource={purchases}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 1000 }}
          size="small"
        />
      </Card>
    </div>
  );
};

export default GstFiling;
